import { useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronCircleDown } from "@fortawesome/free-solid-svg-icons/faChevronCircleDown";
import { resources } from "@constants/hardcoded-strings";
import { FormattedVerificationTransactionDetailsPolicyDecision } from "@idw-types/data/verification-transaction-details";
import { IdwTable } from "../../../common/idw-table/idw-table";
import { IdwTableContainer } from "../../../common/idw-table-container/idw-table-container";
import { PolicyDecisionConditionsResultsContainer } from "./policy-decision-condition-results-container/policy-decision-condition-results-container";
import styles from "./policy-decisions-table-container.less";
import { createColumnHelper, getCoreRowModel, getExpandedRowModel, getSortedRowModel, Row, useReactTable } from "@tanstack/react-table";
import { formatDateToString } from "@utils/date-util";
import { DISPLAY_DATE_WITH_TIME } from "@constants/date-formats";

type PolicyDecisionsTableContainerProps = {
    policyDecisions: Array<FormattedVerificationTransactionDetailsPolicyDecision>;
};

type PolicyDecisionTableDataRow = FormattedVerificationTransactionDetailsPolicyDecision & {
    subRows: Array<any>;
};

export const PolicyDecisionsTableContainer = (props: PolicyDecisionsTableContainerProps) => {
    const { policyDecisions } = props;

    const policyDecisionsWithSubrows = useMemo(() => policyDecisions.map(policyDecision => {
        return {
            ...policyDecision,
            subRows: [{}]
        }
    }), [policyDecisions]);

    const columnHelper = createColumnHelper<PolicyDecisionTableDataRow>();
    const columns = useMemo(() => {
        const finalColumns = [];
        const hasSubrows = policyDecisions.some(pd => pd.policyDecisionConditionResults?.length);

        if (hasSubrows) {
            finalColumns.push(
                columnHelper.display({
                    id: "expander",
                    cell: (props) => {
                        const { row } = props;
                        if (row.original.policyDecisionConditionResults?.length) {
                            return (
                                <button className={styles.chevronButton} id={`policy-decision-expander-${row.index}`} onClick={row.getToggleExpandedHandler()}>
                                    <FontAwesomeIcon className={`${styles.chevron} ${row.getIsExpanded() && styles.rotate}`} icon={faChevronCircleDown} />
                                </button>
                            )
                        }
                        return null;
                    }
                })
            );
        }

        finalColumns.push(
            columnHelper.accessor("date", {
                header: resources.tables.verificationTransactionDetailsPolicyDecisions.columns.date.header,
                cell: (props) => props.getValue() ? <span>{formatDateToString(props.getValue(), DISPLAY_DATE_WITH_TIME)}</span> : null,
            }),
            columnHelper.accessor("rpServiceName", {
                header: resources.tables.verificationTransactionDetailsPolicyDecisions.columns.verificationService.header
            }),
            columnHelper.accessor("apiKey", {
                header: resources.tables.verificationTransactionDetailsPolicyDecisions.columns.apiKey.header
            }),
            columnHelper.accessor("decision", {
                header: resources.tables.verificationTransactionDetailsPolicyDecisions.columns.decision.header
            }),
            columnHelper.accessor("obligationRPServiceName", {
                header: resources.tables.verificationTransactionDetailsPolicyDecisions.columns.stepUpVerificationService.header
            }),
            columnHelper.accessor("obligationAPIKey", {
                header: resources.tables.verificationTransactionDetailsPolicyDecisions.columns.stepUpApiKey.header
            })
        );

        return finalColumns;
    }, [policyDecisions]);

    const useTableResult = useReactTable({
        columns,
        data: policyDecisionsWithSubrows,
        filterFns: null,
        enableSorting: false,
        enableFilters: false,
        sortDescFirst: false,
        getRowCanExpand: (row) => row.original.policyDecisionConditionResults?.length > 0,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getExpandedRowModel: getExpandedRowModel()
    });

    const renderExpandedComponent = (row: Row<FormattedVerificationTransactionDetailsPolicyDecision>) => {
        return <PolicyDecisionConditionsResultsContainer policyDecisionConditionResults={row.original.policyDecisionConditionResults} />
    };

    return (
        <IdwTableContainer className={styles.policyDecisionsTableContainer}>
            <IdwTable
                id="policy-decisions"
                useTableResult={useTableResult}
                headerColumnKeyPrefix={"policy-decisions"}
                renderExpandedComponent={renderExpandedComponent}
                className={styles.policyDecisionsTable}
            />
        </IdwTableContainer>
    );
};